import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SquareArrowOutUpRight, BookOpen, Wheat } from 'lucide-react';

const CHAIN_TYPES = [
  {
    id: 'tehillim',
    title: 'Tehillim',
    icon: BookOpen,
    description: 'Divide the book of Psalms to complete it with others.'
  },
  {
    id: 'challah',
    title: 'Hafrashat Challah',
    icon: Wheat,
    description: 'Dedicate the blessing of separating challah with others.'
  }
];

export default function ChooseChainTypePage() {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState(null);
  const [dedication, setDedication] = useState(null);

  // Device detection for app store redirect
  const handleAppStoreRedirect = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    let appStoreUrl;
    
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      appStoreUrl = 'https://apps.apple.com/us/app/yesod/id6748472269';
    } else if (/android/i.test(userAgent)) {
      appStoreUrl = 'https://play.google.com/store/apps/details?id=us.yesod.app';
    } else {
      appStoreUrl = 'https://apps.apple.com/us/app/yesod/id6748472269';
    }
    
    window.open(appStoreUrl, '_blank');
  };

  useEffect(() => {
    // Load dedication from Step 1
    const savedDedication = sessionStorage.getItem('chainDedication');
    if (!savedDedication) {
      // Redirect back to step 1 if no dedication
      navigate('/create-chain');
      return;
    }
    setDedication(savedDedication);
  }, [navigate]);

  const handleSelect = (typeId) => {
    setSelectedType(typeId);
  };

  const handleBack = () => {
    navigate('/create-chain');
  };

  const handleNext = () => {
    if (selectedType) {
      // Store chain type in sessionStorage
      sessionStorage.setItem('chainType', selectedType);
      // Navigate to details form (Step 3)
      navigate('/create-chain/details');
    }
  };

  const handleClose = () => {
    // Clear session data and go home
    sessionStorage.removeItem('chainDedication');
    sessionStorage.removeItem('chainType');
    navigate('/');
  };

  if (!dedication) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-50 flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-white gap-4">
        <div className="flex items-center gap-3 flex-shrink-0">
          <img 
            src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesod%20Logo%204K%20(1).png" 
            alt="Yesod" 
            className="h-10 w-auto object-contain flex-shrink-0"
          />
          <h1 className="text-xl font-bold text-black m-0 flex-shrink-0">Yesod</h1>
        </div>
        <button 
          onClick={handleAppStoreRedirect}
          className="bg-[#667eea] text-white px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap flex-shrink-0 ml-auto flex items-center gap-1.5 hover:bg-[#5568d3] transition-all cursor-pointer"
        >
          Continue in App
          <SquareArrowOutUpRight size={16} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 px-5 py-8 pb-32">
        <h2 className="text-3xl font-bold text-[#667eea] mb-3 leading-tight">Choose your Chain Type</h2>
        <p className="text-base text-gray-600 mb-8 leading-relaxed">Select the type of chain you want to create</p>

        {/* Chain Type Cards */}
        <div className="flex flex-col gap-4 max-w-2xl mx-auto">
          {CHAIN_TYPES.map((type) => {
            const IconComponent = type.icon;
            const isSelected = selectedType === type.id;
            return (
              <button
                key={type.id}
                className={`bg-gray-50 border-2 rounded-2xl p-5 cursor-pointer transition-all relative flex items-start gap-4 text-left w-full ${
                  isSelected
                    ? 'bg-blue-50 border-[#667eea] border-[3px] shadow-[0_0_0_3px_rgba(102,126,234,0.1)]'
                    : 'border-gray-200 hover:bg-gray-100 hover:border-gray-300 hover:translate-y-[-2px] hover:shadow-md'
                }`}
                onClick={() => handleSelect(type.id)}
              >
                <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-[#667eea] to-[#764ba2] rounded-xl flex items-center justify-center text-white transition-transform hover:scale-105">
                  <IconComponent size={32} strokeWidth={2} />
                </div>
                <div className="flex-1 flex flex-col gap-1.5">
                  <h3 className="text-xl font-bold text-gray-900">{type.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{type.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Footer with Progress and Actions */}
      <div className="sticky bottom-0 left-0 right-0 px-5 py-5 border-t border-gray-200 bg-white flex flex-col gap-4 z-40 mt-auto">
        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#667eea] transition-all"></div>
          <div className="w-8 h-2 rounded bg-[#667eea] transition-all"></div>
          <div className="w-2 h-2 rounded-full bg-gray-300 transition-all"></div>
          <div className="w-2 h-2 rounded-full bg-gray-300 transition-all"></div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between gap-3">
          <button className="bg-transparent text-gray-700 underline hover:text-gray-900 transition-colors" onClick={handleBack}>
            Back
          </button>
          <button
            className="px-6 py-2.5 text-sm font-semibold rounded-lg cursor-pointer transition-all border-none flex items-center justify-center bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white hover:translate-y-[-2px] hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            onClick={handleNext}
            disabled={!selectedType}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
